import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import {
    Save, RefreshCw, ShieldCheck, CheckCircle2, XCircle, AlertTriangle,
    Mail, Smartphone, MessageCircle, ExternalLink, Radio, Filter,
    ChevronDown, ChevronRight, Zap, Clock,
} from "lucide-react";

const CH_ICON = { email: Mail, sms: Smartphone, whatsapp: MessageCircle };
const CH_LABEL = { email: "Email", sms: "SMS", whatsapp: "WhatsApp" };

const STATUS_STYLES = {
    connected:     { icon: CheckCircle2, cls: "text-[#15803D] bg-green-50 border-green-200", label: "Connected" },
    not_connected: { icon: XCircle,      cls: "text-[#64748B] bg-[#F5F7FB] border-[#E5E9F2]", label: "Not connected" },
    error:         { icon: AlertTriangle, cls: "text-[#DC2626] bg-red-50 border-red-200", label: "Error" },
};

function StatusPill({ status, testId }) {
    const s = STATUS_STYLES[status] || STATUS_STYLES.not_connected;
    const Icon = s.icon;
    return (
        <span className={"inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold " + s.cls}
              data-testid={testId}>
            <Icon size={12} /> {s.label}
        </span>
    );
}

function Card({ children, className = "" }) {
    return <div className={"ddi-card ddi-shadow-sm p-6 " + className}>{children}</div>;
}

function Field({ label, hint, children }) {
    return (
        <div>
            <label className="ddi-input-label">{label}</label>
            {children}
            {hint && <div className="text-[11px] text-[#64748B] mt-1">{hint}</div>}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════
// Tab 1 · Connections
// ═══════════════════════════════════════════════════════════════════════
function ConnectionsTab() {
    const [cfg, setCfg] = useState(null);
    const [busy, setBusy] = useState({});

    // Drafts
    const [wa, setWA] = useState({ phone_number_id: "", waba_id: "", access_token: "", api_base_url: "", api_version: "", enabled: false });
    const [email, setEmail] = useState({ api_key: "", from_email: "", from_name: "", enabled: false });
    const [sms, setSMS] = useState({
        provider: "twilio", enabled: false,
        twilio: { account_sid: "", auth_token: "", from_number: "" },
        msg91: { auth_key: "", sender_id: "", dlt_template_id: "" },
    });

    useEffect(() => { load(); }, []);
    async function load() {
        try {
            const r = await api.get("/super-admin/comms-center/connections");
            setCfg(r.data);
            setWA({
                phone_number_id: r.data.wa.phone_number_id || "",
                waba_id: r.data.wa.waba_id || "",
                access_token: "",
                api_base_url: r.data.wa.api_base_url || "https://graph.facebook.com",
                api_version: r.data.wa.api_version || "v20.0",
                enabled: !!r.data.wa.enabled,
            });
            setEmail({
                api_key: "",
                from_email: r.data.email.from_email || "",
                from_name: r.data.email.from_name || "",
                enabled: !!r.data.email.enabled,
            });
            setSMS({
                provider: r.data.sms.provider || "twilio",
                enabled: !!r.data.sms.enabled,
                twilio: {
                    account_sid: r.data.sms.twilio.account_sid || "",
                    auth_token: "",
                    from_number: r.data.sms.twilio.from_number || "",
                },
                msg91: {
                    auth_key: "",
                    sender_id: r.data.sms.msg91.sender_id || "",
                    dlt_template_id: r.data.sms.msg91.dlt_template_id || "",
                },
            });
        } catch (e) { toast.error(errMsg(e, "Failed to load config")); }
    }

    async function save(section, payload) {
        setBusy((b) => ({ ...b, [`save-${section}`]: true }));
        try {
            const r = await api.put(`/super-admin/comms-center/connections/${section}`, payload);
            setCfg(r.data);
            toast.success(`${section.toUpperCase()} saved`);
        } catch (e) { toast.error(errMsg(e, "Save failed")); }
        finally { setBusy((b) => ({ ...b, [`save-${section}`]: false })); }
    }

    async function saveAndVerify(section, payload) {
        setBusy((b) => ({ ...b, [`ver-${section}`]: true }));
        try {
            await api.put(`/super-admin/comms-center/connections/${section}`, payload);
            const r = await api.post(`/super-admin/comms-center/connections/${section}/verify`);
            const view = await api.get("/super-admin/comms-center/connections");
            setCfg(view.data);
            (r.data.status === "connected" ? toast.success : toast.error)(
                `${section.toUpperCase()}: ${r.data.message}`
            );
        } catch (e) { toast.error(errMsg(e, "Verify failed")); }
        finally { setBusy((b) => ({ ...b, [`ver-${section}`]: false })); }
    }

    if (!cfg) return (
        <div className="space-y-4">
            <div className="ddi-skeleton h-40 w-full" />
            <div className="ddi-skeleton h-40 w-full" />
            <div className="ddi-skeleton h-40 w-full" />
        </div>
    );

    return (
        <div className="space-y-6" data-testid="comms-connections">
            {/* WhatsApp */}
            <Card>
                <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                    <div className="flex items-start gap-3">
                        <MessageCircle size={22} className="text-[#0F766E] mt-0.5" />
                        <div>
                            <h2 className="text-lg font-bold text-[#123B5D]">WhatsApp Business (WABA)</h2>
                            <p className="text-sm text-[#64748B] mt-0.5">Meta Cloud API — or any Meta-payload-compatible gateway. Outbound utility &amp; marketing templates.</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <StatusPill status={cfg.wa.status} testId="wa-status" />
                        <a href="https://developers.facebook.com/docs/whatsapp/cloud-api" target="_blank" rel="noopener noreferrer"
                           className="inline-flex items-center gap-1 text-xs font-semibold text-[#2563EB] hover:underline">
                            Docs <ExternalLink size={11} />
                        </a>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Phone Number ID">
                        <input className="ddi-input" value={wa.phone_number_id}
                               onChange={(e) => setWA({ ...wa, phone_number_id: e.target.value })}
                               placeholder="Numeric ID from Meta Business Manager"
                               data-testid="wa-phone-number-id" />
                    </Field>
                    <Field label="WhatsApp Business Account ID">
                        <input className="ddi-input" value={wa.waba_id}
                               onChange={(e) => setWA({ ...wa, waba_id: e.target.value })}
                               placeholder="Optional but recommended"
                               data-testid="wa-waba-id" />
                    </Field>
                    <Field label="Access Token"
                           hint={cfg.wa.masked_token ? `Saved: ${cfg.wa.masked_token} — leave blank to keep.` : "System-user permanent access token."}>
                        <input type="password" className="ddi-input" value={wa.access_token}
                               onChange={(e) => setWA({ ...wa, access_token: e.target.value })}
                               placeholder={cfg.wa.masked_token ? "•••• leave blank to keep existing" : "EAAB…"}
                               data-testid="wa-access-token" />
                    </Field>
                    <Field label="Enable channel">
                        <label className="inline-flex items-center gap-2 mt-2 cursor-pointer">
                            <input type="checkbox" checked={wa.enabled}
                                   onChange={(e) => setWA({ ...wa, enabled: e.target.checked })}
                                   data-testid="wa-enabled" />
                            <span className="text-sm text-[#123B5D] font-medium">Route WhatsApp touchpoints via this account</span>
                        </label>
                    </Field>
                    <Field label="API Base URL"
                           hint="Default: https://graph.facebook.com (Meta Graph). Change to point at a Meta-compatible gateway (e.g. whatapi.in).">
                        <input className="ddi-input" value={wa.api_base_url}
                               onChange={(e) => setWA({ ...wa, api_base_url: e.target.value })}
                               placeholder="https://graph.facebook.com"
                               data-testid="wa-api-base-url" />
                    </Field>
                    <Field label="API Version"
                           hint="Meta Graph: v20.0. Some gateways still use v19.0.">
                        <input className="ddi-input" value={wa.api_version}
                               onChange={(e) => setWA({ ...wa, api_version: e.target.value })}
                               placeholder="v20.0"
                               data-testid="wa-api-version" />
                    </Field>
                </div>
                <div className="mt-4 flex gap-2 flex-wrap">
                    <button className="ddi-teal-btn text-sm" disabled={!!busy["save-wa"]}
                            onClick={() => save("wa", wa)} data-testid="wa-save">
                        <Save size={13} className="mr-1.5" /> {busy["save-wa"] ? "Saving…" : "Save"}
                    </button>
                    <button className="ddi-btn-outline text-sm" disabled={!!busy["ver-wa"]}
                            onClick={() => saveAndVerify("wa", wa)} data-testid="wa-verify">
                        <RefreshCw size={13} className={"mr-1.5 " + (busy["ver-wa"] ? "animate-spin" : "")} />
                        {busy["ver-wa"] ? "Verifying…" : "Save & Verify"}
                    </button>
                </div>
                {cfg.wa.last_error && (
                    <div className="ddi-error-text mt-3" data-testid="wa-error">
                        <AlertTriangle size={13} /> {cfg.wa.last_error}
                    </div>
                )}
                {cfg.wa.verified_name && (
                    <div className="text-xs text-[#64748B] mt-2" data-testid="wa-verified-info">
                        Verified name: <b className="text-[#123B5D]">{cfg.wa.verified_name}</b>
                        {cfg.wa.display_phone_number ? <span> · {cfg.wa.display_phone_number}</span> : null}
                        {!cfg.wa.is_default_gateway && cfg.wa.api_base_url ? (
                            <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#F5F8FA] px-2 py-0.5 text-[10px] uppercase tracking-widest text-[#0F766E] font-semibold" data-testid="wa-custom-gateway-pill">
                                Custom gateway
                            </span>
                        ) : null}
                    </div>
                )}
            </Card>

            {/* Email (Resend) */}
            <Card>
                <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                    <div className="flex items-start gap-3">
                        <Mail size={22} className="text-[#0F766E] mt-0.5" />
                        <div>
                            <h2 className="text-lg font-bold text-[#123B5D]">Email (Resend)</h2>
                            <p className="text-sm text-[#64748B] mt-0.5">Transactional email via Resend API. Verify method: GET /domains.</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <StatusPill status={cfg.email.status} testId="email-status" />
                        <a href="https://resend.com/docs/api-reference/introduction" target="_blank" rel="noopener noreferrer"
                           className="inline-flex items-center gap-1 text-xs font-semibold text-[#2563EB] hover:underline">
                            Docs <ExternalLink size={11} />
                        </a>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Resend API key"
                           hint={cfg.email.masked_key ? `Saved: ${cfg.email.masked_key} — leave blank to keep.` : "Starts with re_"}>
                        <input type="password" className="ddi-input" value={email.api_key}
                               onChange={(e) => setEmail({ ...email, api_key: e.target.value })}
                               placeholder={cfg.email.masked_key ? "•••• leave blank to keep existing" : "re_…"}
                               data-testid="email-api-key" />
                    </Field>
                    <Field label="Enable channel">
                        <label className="inline-flex items-center gap-2 mt-2 cursor-pointer">
                            <input type="checkbox" checked={email.enabled}
                                   onChange={(e) => setEmail({ ...email, enabled: e.target.checked })}
                                   data-testid="email-enabled" />
                            <span className="text-sm text-[#123B5D] font-medium">Route email touchpoints via this key</span>
                        </label>
                    </Field>
                    <Field label="From email">
                        <input className="ddi-input" value={email.from_email}
                               onChange={(e) => setEmail({ ...email, from_email: e.target.value })}
                               placeholder="no-reply@yourdomain.com"
                               data-testid="email-from" />
                    </Field>
                    <Field label="From name">
                        <input className="ddi-input" value={email.from_name}
                               onChange={(e) => setEmail({ ...email, from_name: e.target.value })}
                               placeholder="Doctor Digital Identity"
                               data-testid="email-from-name" />
                    </Field>
                </div>
                <div className="mt-4 flex gap-2 flex-wrap">
                    <button className="ddi-teal-btn text-sm" disabled={!!busy["save-email"]}
                            onClick={() => save("email", email)} data-testid="email-save">
                        <Save size={13} className="mr-1.5" /> {busy["save-email"] ? "Saving…" : "Save"}
                    </button>
                    <button className="ddi-btn-outline text-sm" disabled={!!busy["ver-email"]}
                            onClick={() => saveAndVerify("email", email)} data-testid="email-verify">
                        <RefreshCw size={13} className={"mr-1.5 " + (busy["ver-email"] ? "animate-spin" : "")} />
                        {busy["ver-email"] ? "Verifying…" : "Save & Verify"}
                    </button>
                </div>
                {cfg.email.last_error && (
                    <div className="ddi-error-text mt-3" data-testid="email-error">
                        <AlertTriangle size={13} /> {cfg.email.last_error}
                    </div>
                )}
                {cfg.email.domains_verified && cfg.email.domains_verified.length > 0 && (
                    <div className="text-xs text-[#64748B] mt-2">
                        Verified domains: <b className="text-[#15803D]">{cfg.email.domains_verified.join(", ")}</b>
                    </div>
                )}
            </Card>

            {/* SMS */}
            <Card>
                <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                    <div className="flex items-start gap-3">
                        <Smartphone size={22} className="text-[#0F766E] mt-0.5" />
                        <div>
                            <h2 className="text-lg font-bold text-[#123B5D]">SMS</h2>
                            <p className="text-sm text-[#64748B] mt-0.5">Choose Twilio (global) or MSG91 (India DLT). Only one provider active at a time.</p>
                        </div>
                    </div>
                    <StatusPill status={cfg.sms.status} testId="sms-status" />
                </div>
                <div className="mb-4 flex items-center gap-3 flex-wrap">
                    <Field label="Active provider">
                        <select className="ddi-input" value={sms.provider}
                                onChange={(e) => setSMS({ ...sms, provider: e.target.value })}
                                data-testid="sms-provider">
                            <option value="twilio">Twilio</option>
                            <option value="msg91">MSG91 (India DLT)</option>
                        </select>
                    </Field>
                    <div className="ml-auto">
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={sms.enabled}
                                   onChange={(e) => setSMS({ ...sms, enabled: e.target.checked })}
                                   data-testid="sms-enabled" />
                            <span className="text-sm text-[#123B5D] font-medium">Route SMS touchpoints</span>
                        </label>
                    </div>
                </div>

                {sms.provider === "twilio" ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="sms-twilio-form">
                        <Field label="Account SID">
                            <input className="ddi-input" value={sms.twilio.account_sid}
                                   onChange={(e) => setSMS({ ...sms, twilio: { ...sms.twilio, account_sid: e.target.value } })}
                                   placeholder="ACxxxxxxxxxxxxxxxx"
                                   data-testid="sms-twilio-sid" />
                        </Field>
                        <Field label="Auth Token"
                               hint={cfg.sms.twilio.masked_token ? `Saved: ${cfg.sms.twilio.masked_token} — leave blank to keep.` : ""}>
                            <input type="password" className="ddi-input" value={sms.twilio.auth_token}
                                   onChange={(e) => setSMS({ ...sms, twilio: { ...sms.twilio, auth_token: e.target.value } })}
                                   placeholder={cfg.sms.twilio.masked_token ? "•••• leave blank to keep existing" : "…"}
                                   data-testid="sms-twilio-token" />
                        </Field>
                        <Field label="Sender / From number">
                            <input className="ddi-input" value={sms.twilio.from_number}
                                   onChange={(e) => setSMS({ ...sms, twilio: { ...sms.twilio, from_number: e.target.value } })}
                                   placeholder="+1XXXXXXXXXX or +91XXXXXXXXXX"
                                   data-testid="sms-twilio-from" />
                        </Field>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="sms-msg91-form">
                        <Field label="Auth Key"
                               hint={cfg.sms.msg91.masked_key ? `Saved: ${cfg.sms.msg91.masked_key} — leave blank to keep.` : ""}>
                            <input type="password" className="ddi-input" value={sms.msg91.auth_key}
                                   onChange={(e) => setSMS({ ...sms, msg91: { ...sms.msg91, auth_key: e.target.value } })}
                                   placeholder={cfg.sms.msg91.masked_key ? "•••• leave blank to keep existing" : "your MSG91 auth key"}
                                   data-testid="sms-msg91-authkey" />
                        </Field>
                        <Field label="Sender ID (6 chars)">
                            <input className="ddi-input" value={sms.msg91.sender_id}
                                   onChange={(e) => setSMS({ ...sms, msg91: { ...sms.msg91, sender_id: e.target.value } })}
                                   placeholder="DDIPIN"
                                   data-testid="sms-msg91-sender" />
                        </Field>
                        <Field label="DLT Template ID">
                            <input className="ddi-input" value={sms.msg91.dlt_template_id}
                                   onChange={(e) => setSMS({ ...sms, msg91: { ...sms.msg91, dlt_template_id: e.target.value } })}
                                   placeholder="1707xxxxxxxxxx"
                                   data-testid="sms-msg91-dlt" />
                        </Field>
                    </div>
                )}

                <div className="mt-4 flex gap-2 flex-wrap">
                    <button className="ddi-teal-btn text-sm" disabled={!!busy["save-sms"]}
                            onClick={() => save("sms", sms)} data-testid="sms-save">
                        <Save size={13} className="mr-1.5" /> {busy["save-sms"] ? "Saving…" : "Save"}
                    </button>
                    <button className="ddi-btn-outline text-sm" disabled={!!busy["ver-sms"]}
                            onClick={() => saveAndVerify("sms", sms)} data-testid="sms-verify">
                        <RefreshCw size={13} className={"mr-1.5 " + (busy["ver-sms"] ? "animate-spin" : "")} />
                        {busy["ver-sms"] ? "Verifying…" : "Save & Verify"}
                    </button>
                </div>
                {cfg.sms.last_error && (
                    <div className="ddi-error-text mt-3" data-testid="sms-error">
                        <AlertTriangle size={13} /> {cfg.sms.last_error}
                    </div>
                )}
            </Card>

            <div className="ddi-card ddi-shadow-xs p-4 bg-[#F5F7FB] border-[#3A80F7]/15 flex items-start gap-3">
                <ShieldCheck size={16} className="text-[#0F766E] mt-0.5 shrink-0" />
                <div className="text-[13px] text-[#17212B]/80">
                    <span className="font-semibold text-[#123B5D]">Fail-safe:</span> When a provider is disconnected or a touchpoint channel is toggled off, the dispatcher logs the attempt as <b>skipped</b> in the delivery log — <b>never crashing</b> the flow that triggered it. OTP delivery continues via whichever provider is available (email fallback if SMS/WhatsApp aren't wired).
                </div>
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════
// Tab 2 · Touchpoint Matrix
// ═══════════════════════════════════════════════════════════════════════
function TouchpointsTab() {
    const [rows, setRows] = useState(null);
    const [busy, setBusy] = useState({});
    const [expanded, setExpanded] = useState(null); // "{code}:{channel}"
    const [editor, setEditor] = useState(null); // {template obj}
    const [savingTpl, setSavingTpl] = useState(false);

    useEffect(() => { load(); }, []);
    async function load() {
        try {
            const r = await api.get("/super-admin/comms-center/touchpoints");
            setRows(r.data.items);
        } catch (e) { toast.error(errMsg(e, "Failed to load matrix")); }
    }

    async function toggle(code, channel, next) {
        setBusy((b) => ({ ...b, [`${code}:${channel}`]: true }));
        try {
            const r = await api.put(`/super-admin/comms-center/touchpoints/${code}`, { [channel]: next });
            setRows((old) => old.map((row) =>
                row.code === code ? { ...row, toggles: { ...row.toggles, [channel]: !!r.data[channel] } } : row));
        } catch (e) { toast.error(errMsg(e, "Toggle failed")); }
        finally { setBusy((b) => ({ ...b, [`${code}:${channel}`]: false })); }
    }

    async function openEditor(code, channel) {
        try {
            const r = await api.get(`/super-admin/comms-center/templates/${code}/${channel}`);
            setEditor({ code, channel, template: r.data.template });
        } catch (e) { toast.error(errMsg(e, "Failed to load template")); }
    }

    async function saveTemplate() {
        if (!editor?.template?.id) {
            toast.error("Template not persisted yet — a save creates the row on first edit.");
            // Note: default_fallback templates have no id. In our seed_defaults
            // we insert on startup, so this is rare.
            return;
        }
        setSavingTpl(true);
        try {
            const t = editor.template;
            const patch = { enabled: t.enabled, subject: t.subject, body: t.body,
                            header: t.header, footer: t.footer, buttons: t.buttons };
            await api.put(`/super-admin/comms-center/templates/${t.id}`, patch);
            toast.success("Template saved");
            setEditor(null);
            load();
        } catch (e) { toast.error(errMsg(e, "Save failed")); }
        finally { setSavingTpl(false); }
    }

    if (!rows) return (
        <div className="space-y-3">
            {[...Array(6)].map((_, i) => <div key={i} className="ddi-skeleton h-14 w-full" />)}
        </div>
    );

    return (
        <div className="space-y-4" data-testid="comms-matrix">
            <div className="text-xs text-[#64748B]">
                <b>{rows.length}</b> touchpoints × 3 channels. Toggle a channel off to keep the flow running without messaging on that channel. Click <b>Edit</b> to customise the template body.
            </div>
            <div className="ddi-card ddi-shadow-sm overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-[#F5F8FA] text-[#123B5D]">
                        <tr>
                            <th className="text-left px-3 py-2.5 font-semibold">Touchpoint</th>
                            <th className="text-center px-3 py-2.5 font-semibold w-24">Email</th>
                            <th className="text-center px-3 py-2.5 font-semibold w-24">SMS</th>
                            <th className="text-center px-3 py-2.5 font-semibold w-24">WhatsApp</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <>
                                <tr key={row.code} className="border-t border-[#E5E9F2] hover:bg-[#F5F8FA]/60"
                                    data-testid={`matrix-row-${row.code}`}>
                                    <td className="px-3 py-3">
                                        <div className="font-semibold text-[#123B5D] flex items-center gap-1.5">
                                            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-[#0F766E] bg-[#0F766E]/10 rounded px-1.5 py-0.5">
                                                {row.stage}
                                            </span>
                                            {row.label}
                                        </div>
                                        <div className="text-xs text-[#64748B] mt-0.5">{row.description}</div>
                                        <div className="text-[10px] text-[#94A3B8] mt-0.5 font-mono">{row.code}</div>
                                    </td>
                                    {["email", "sms", "whatsapp"].map((ch) => {
                                        const CI = CH_ICON[ch];
                                        const on = !!row.toggles[ch];
                                        const key = `${row.code}:${ch}`;
                                        return (
                                            <td key={ch} className="text-center px-3 py-3">
                                                <div className="flex flex-col items-center gap-1">
                                                    <label className="inline-flex items-center gap-1.5 cursor-pointer">
                                                        <input type="checkbox" checked={on} disabled={!!busy[key]}
                                                               onChange={(e) => toggle(row.code, ch, e.target.checked)}
                                                               data-testid={`toggle-${row.code}-${ch}`} />
                                                        <CI size={12} className={on ? "text-[#0F766E]" : "text-[#94A3B8]"} />
                                                    </label>
                                                    <button className="text-[10px] font-semibold text-[#2563EB] hover:underline"
                                                            onClick={() => openEditor(row.code, ch)}
                                                            data-testid={`edit-${row.code}-${ch}`}>
                                                        Edit
                                                    </button>
                                                </div>
                                            </td>
                                        );
                                    })}
                                </tr>
                            </>
                        ))}
                    </tbody>
                </table>
            </div>

            {editor && (
                <TemplateEditorModal editor={editor} onClose={() => setEditor(null)}
                                     onSave={saveTemplate} saving={savingTpl}
                                     onPatch={(patch) => setEditor((e) => ({ ...e, template: { ...e.template, ...patch } }))} />
            )}
        </div>
    );
}

function TemplateEditorModal({ editor, onClose, onSave, saving, onPatch }) {
    const t = editor.template;
    const ch = editor.channel;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
             style={{ background: "rgba(15, 23, 42, 0.5)" }}
             data-testid="template-editor-modal">
            <div className="ddi-card ddi-shadow-lg bg-white max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6">
                <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                        <h3 className="text-lg font-bold text-[#123B5D]">
                            {editor.code} · <span className="text-[#0F766E]">{CH_LABEL[ch]}</span>
                        </h3>
                        <div className="text-xs text-[#64748B] mt-0.5">
                            Variables:{" "}
                            {(t.vars || []).map((v) => (
                                <code key={v} className="text-[11px] bg-[#F5F8FA] px-1.5 py-0.5 rounded mr-1">{`{{${v}}}`}</code>
                            ))}
                        </div>
                    </div>
                    <button className="text-[#64748B] hover:text-[#DC2626] text-lg" onClick={onClose}
                            data-testid="template-editor-close">✕</button>
                </div>
                <div className="space-y-3">
                    <label className="inline-flex items-center gap-2 cursor-pointer text-sm">
                        <input type="checkbox" checked={!!t.enabled}
                               onChange={(e) => onPatch({ enabled: e.target.checked })}
                               data-testid="template-enabled" />
                        <span className="font-medium">Template enabled</span>
                    </label>
                    {ch === "email" && (
                        <Field label="Subject">
                            <input className="ddi-input" value={t.subject || ""}
                                   onChange={(e) => onPatch({ subject: e.target.value })}
                                   data-testid="template-subject" />
                        </Field>
                    )}
                    {ch === "whatsapp" && (
                        <Field label="Header (optional)">
                            <input className="ddi-input" value={t.header || ""}
                                   onChange={(e) => onPatch({ header: e.target.value })}
                                   data-testid="template-header" />
                        </Field>
                    )}
                    <Field label="Body">
                        <textarea className="ddi-input min-h-[180px] font-mono text-[13px]" value={t.body || ""}
                                  onChange={(e) => onPatch({ body: e.target.value })}
                                  data-testid="template-body" />
                    </Field>
                    {t.source === "default_fallback" && (
                        <div className="text-[11px] text-[#D97706] flex items-start gap-1.5">
                            <AlertTriangle size={12} className="mt-0.5" />
                            This template is a seeded default — saving will persist your edits to the DB.
                        </div>
                    )}
                </div>
                <div className="mt-5 flex gap-2 justify-end">
                    <button className="ddi-btn-outline text-sm" onClick={onClose}
                            data-testid="template-cancel">Cancel</button>
                    <button className="ddi-teal-btn text-sm" disabled={saving} onClick={onSave}
                            data-testid="template-save">
                        <Save size={13} className="mr-1.5" /> {saving ? "Saving…" : "Save changes"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════
// Tab 3 · Delivery Log
// ═══════════════════════════════════════════════════════════════════════
function DeliveryLogTab() {
    const [data, setData] = useState({ total: 0, items: [], page: 1, page_size: 50 });
    const [summary, setSummary] = useState(null);
    const [filters, setFilters] = useState({ channel: "", status: "", touchpoint: "",
                                              from_date: "", to_date: "" });
    const [loading, setLoading] = useState(false);
    const [groups, setGroups] = useState([]);

    useEffect(() => {
        api.get("/super-admin/comms-center/delivery-log/summary").then((r) => setSummary(r.data)).catch(() => {});
        api.get("/super-admin/comms-center/touchpoints").then((r) => setGroups(r.data.items)).catch(() => {});
        load(1);
    }, []);

    async function load(page) {
        setLoading(true);
        try {
            const q = new URLSearchParams();
            q.set("page", page);
            q.set("page_size", data.page_size);
            for (const k of ["channel", "status", "touchpoint", "from_date", "to_date"]) {
                if (filters[k]) q.set(k, filters[k]);
            }
            const r = await api.get(`/super-admin/comms-center/delivery-log?${q.toString()}`);
            setData(r.data);
        } catch (e) { toast.error(errMsg(e, "Failed to load delivery log")); }
        finally { setLoading(false); }
    }

    const totalPages = Math.max(1, Math.ceil((data.total || 0) / (data.page_size || 50)));

    return (
        <div className="space-y-4" data-testid="comms-delivery-log">
            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="ddi-card ddi-shadow-xs p-3">
                        <div className="text-[11px] text-[#64748B] uppercase tracking-wide">Last 7 days</div>
                        <div className="text-2xl font-bold text-[#123B5D]">{summary.total}</div>
                    </div>
                    {["sent", "skipped", "error"].map((s) => (
                        <div key={s} className="ddi-card ddi-shadow-xs p-3">
                            <div className="text-[11px] text-[#64748B] uppercase tracking-wide">{s}</div>
                            <div className={"text-2xl font-bold " +
                                (s === "sent" ? "text-[#15803D]" :
                                 s === "error" ? "text-[#DC2626]" : "text-[#D97706]")}>
                                {summary.by_status?.[s] || 0}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="ddi-card ddi-shadow-xs p-4">
                <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-[#123B5D]">
                    <Filter size={14} /> Filters
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
                    <select className="ddi-input" value={filters.channel}
                            onChange={(e) => setFilters({ ...filters, channel: e.target.value })}
                            data-testid="log-filter-channel">
                        <option value="">All channels</option>
                        <option value="email">Email</option>
                        <option value="sms">SMS</option>
                        <option value="whatsapp">WhatsApp</option>
                    </select>
                    <select className="ddi-input" value={filters.status}
                            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                            data-testid="log-filter-status">
                        <option value="">All statuses</option>
                        <option value="sent">Sent</option>
                        <option value="skipped">Skipped</option>
                        <option value="error">Error</option>
                        <option value="disabled">Disabled</option>
                        <option value="opted_out">Opted out</option>
                    </select>
                    <select className="ddi-input" value={filters.touchpoint}
                            onChange={(e) => setFilters({ ...filters, touchpoint: e.target.value })}
                            data-testid="log-filter-touchpoint">
                        <option value="">All touchpoints</option>
                        {groups.flatMap((g) => g.member_keys).map((k) =>
                            <option key={k} value={k}>{k}</option>)}
                    </select>
                    <input type="date" className="ddi-input" value={filters.from_date}
                           onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
                           data-testid="log-filter-from" />
                    <input type="date" className="ddi-input" value={filters.to_date}
                           onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
                           data-testid="log-filter-to" />
                </div>
                <div className="mt-3 flex gap-2">
                    <button className="ddi-teal-btn text-sm" onClick={() => load(1)}
                            data-testid="log-apply">
                        {loading ? "Loading…" : "Apply filters"}
                    </button>
                    <button className="ddi-btn-outline text-sm"
                            onClick={() => { setFilters({ channel: "", status: "", touchpoint: "",
                                                         from_date: "", to_date: "" });
                                             setTimeout(() => load(1), 50); }}
                            data-testid="log-clear">Clear</button>
                </div>
            </div>

            <div className="ddi-card ddi-shadow-sm overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-[#F5F8FA] text-[#123B5D]">
                        <tr>
                            <th className="text-left px-3 py-2.5 font-semibold">Time</th>
                            <th className="text-left px-3 py-2.5 font-semibold">Recipient</th>
                            <th className="text-left px-3 py-2.5 font-semibold">Touchpoint</th>
                            <th className="text-left px-3 py-2.5 font-semibold">Channel</th>
                            <th className="text-left px-3 py-2.5 font-semibold">Status</th>
                            <th className="text-left px-3 py-2.5 font-semibold">Detail</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.items.length === 0 && !loading && (
                            <tr><td colSpan={6} className="text-center py-10 text-[#64748B]">No delivery entries match your filters.</td></tr>
                        )}
                        {data.items.map((row) => (
                            <tr key={row.id} className="border-t border-[#E5E9F2]" data-testid={`log-row-${row.id}`}>
                                <td className="px-3 py-2 whitespace-nowrap text-xs text-[#64748B]">
                                    {(row.created_at || "").replace("T", " ").slice(0, 19)}
                                </td>
                                <td className="px-3 py-2 font-mono text-xs text-[#123B5D]">{row.recipient_masked}</td>
                                <td className="px-3 py-2 text-xs">
                                    <div className="font-semibold text-[#123B5D]">{row.group_label || "—"}</div>
                                    <div className="font-mono text-[10px] text-[#94A3B8]">{row.touchpoint_key}</div>
                                </td>
                                <td className="px-3 py-2 text-xs">{row.channel}</td>
                                <td className="px-3 py-2 text-xs">
                                    <span className={"inline-flex px-2 py-0.5 rounded text-[11px] font-semibold " +
                                        (row.status === "sent" ? "bg-green-100 text-[#15803D]" :
                                         row.status === "error" ? "bg-red-100 text-[#DC2626]" :
                                         "bg-slate-100 text-[#64748B]")}>
                                        {row.status}
                                    </span>
                                </td>
                                <td className="px-3 py-2 text-xs text-[#64748B] max-w-[380px] truncate" title={JSON.stringify(row.detail || "")}>
                                    {typeof row.detail === "string" ? row.detail : JSON.stringify(row.detail || "")}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center justify-between text-xs text-[#64748B]">
                <div>Page {data.page} / {totalPages} · {data.total} rows total</div>
                <div className="flex gap-2">
                    <button className="ddi-btn-outline text-xs" disabled={data.page <= 1 || loading}
                            onClick={() => load(data.page - 1)} data-testid="log-prev">Prev</button>
                    <button className="ddi-btn-outline text-xs" disabled={data.page >= totalPages || loading}
                            onClick={() => load(data.page + 1)} data-testid="log-next">Next</button>
                </div>
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════
// Page shell
// ═══════════════════════════════════════════════════════════════════════
const TABS = [
    { id: "connections",     label: "Connections",       icon: Radio },
    { id: "touchpoints",     label: "Touchpoint matrix", icon: Zap },
    { id: "wa_templates",    label: "WhatsApp templates",icon: MessageCircle },
    { id: "delivery",        label: "Delivery log",      icon: Clock },
];

// ═══════════════════════════════════════════════════════════════════════
// Tab · WhatsApp per-touchpoint template mapper (Iter74)
// ═══════════════════════════════════════════════════════════════════════
function WaTemplateMapperTab() {
    const [rows, setRows] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [waConfigured, setWaConfigured] = useState(false);
    const [drafts, setDrafts] = useState({});      // {code: {template_name, language, variable_names[]}}
    const [testTo, setTestTo] = useState({});      // {code: "919…"}
    const [busy, setBusy] = useState({});
    const [ruleState, setRuleState] = useState(null);
    const [tplStatus, setTplStatus] = useState([]);   // catalog + live status
    const [statusBusy, setStatusBusy] = useState(false);
    const [bulkResult, setBulkResult] = useState(null);

    useEffect(() => { load(); loadRules(); loadTplStatus(); }, []);

    async function load() {
        try {
            const r = await api.get("/super-admin/whatsapp/template-mappings");
            setRows(r.data.items || []);
            setTemplates(r.data.templates || []);
            setWaConfigured(!!r.data.wa_configured);
            const d = {};
            for (const row of r.data.items || []) {
                d[row.code] = {
                    template_name: row.template_name || "",
                    language: row.language || "en",
                    variable_names: row.variable_names || [],
                };
            }
            setDrafts(d);
        } catch (e) { toast.error(errMsg(e, "Failed to load mappings")); }
    }

    async function loadRules() {
        try {
            const r = await api.get("/super-admin/automation-templates");
            setRuleState(r.data.items || []);
        } catch (e) { /* not critical */ }
    }

    async function loadTplStatus() {
        try {
            const r = await api.get("/super-admin/whatsapp/templates/status");
            setTplStatus(r.data.items || []);
        } catch (e) { /* not critical — happens before submit */ }
    }

    async function refreshStatuses() {
        setStatusBusy(true);
        try {
            const r = await api.post("/super-admin/whatsapp/templates/refresh-statuses");
            if (r.data.ok === false) { toast.error(errMsg(r.data, "Refresh failed")); return; }
            toast.success(`Refreshed ${r.data.count || 0} live templates`);
            await Promise.all([loadTplStatus(), load()]);
        } catch (e) { toast.error(errMsg(e, "Refresh failed")); }
        finally { setStatusBusy(false); }
    }

    async function bulkSubmit() {
        if (!window.confirm("Submit every template in the catalog to the WABA?\nThis is idempotent — already-existing templates are skipped.")) return;
        setStatusBusy(true);
        try {
            const r = await api.post("/super-admin/whatsapp/templates/bulk-create", {});
            setBulkResult(r.data);
            toast.success(`Submitted ${r.data.count || 0} templates · panel_md_path=${r.data.panel_md_path || "n/a"}`);
            await Promise.all([loadTplStatus(), load()]);
        } catch (e) { toast.error(errMsg(e, "Bulk submit failed")); }
        finally { setStatusBusy(false); }
    }

    async function preWireMappings() {
        if (!window.confirm("Pre-wire every touchpoint → template mapping from the shipping plan?\nExisting mappings will be overwritten.")) return;
        try {
            const r = await api.post("/super-admin/whatsapp/mappings/pre-wire");
            toast.success(`Pre-wired ${r.data.saved?.length || 0} mappings`);
            await load();
        } catch (e) { toast.error(errMsg(e, "Pre-wire failed")); }
    }

    async function save(code) {
        setBusy((b) => ({ ...b, [`save-${code}`]: true }));
        try {
            await api.put(`/super-admin/whatsapp/template-mappings/${code}`, drafts[code] || {});
            toast.success(`Saved ${code}`);
            await load();
        } catch (e) { toast.error(errMsg(e, "Save failed")); }
        finally { setBusy((b) => ({ ...b, [`save-${code}`]: false })); }
    }

    async function clear(code) {
        if (!window.confirm(`Clear the template mapping for "${code}"?`)) return;
        setBusy((b) => ({ ...b, [`save-${code}`]: true }));
        try {
            await api.put(`/super-admin/whatsapp/template-mappings/${code}`, { template_name: "" });
            toast.success("Cleared");
            await load();
        } catch (e) { toast.error(errMsg(e, "Clear failed")); }
        finally { setBusy((b) => ({ ...b, [`save-${code}`]: false })); }
    }

    async function testSend(row) {
        const to = (testTo[row.code] || "").trim();
        if (!to) { toast.error("Enter a phone number first"); return; }
        setBusy((b) => ({ ...b, [`test-${row.code}`]: true }));
        try {
            const vars = {};
            for (const name of drafts[row.code]?.variable_names || []) vars[name] = "sample-" + name;
            const r = await api.post(`/super-admin/whatsapp/template-mappings/${row.code}/test-send`,
                                       { to, variables: vars });
            toast.success(`Sent · id ${(r.data.message_id || "").slice(0, 8)}…`);
        } catch (e) { toast.error(errMsg(e, "Test-send failed")); }
        finally { setBusy((b) => ({ ...b, [`test-${row.code}`]: false })); }
    }

    async function installRule(key) {
        try {
            await api.post(`/super-admin/automation-templates/${key}/install`);
            toast.success("Automation rule installed and enabled");
            await loadRules();
        } catch (e) { toast.error(errMsg(e, "Install failed")); }
    }

    function updateDraft(code, patch) {
        setDrafts((d) => ({ ...d, [code]: { ...(d[code] || {}), ...patch } }));
    }

    function updateVarAt(code, i, value) {
        const arr = [...(drafts[code]?.variable_names || [])];
        arr[i] = value;
        updateDraft(code, { variable_names: arr });
    }

    // Aggregate live status counts for the header
    const statusCounts = tplStatus.reduce((acc, t) => {
        const s = (t.live_status || t.submission_status || "unknown").toString().toUpperCase();
        acc[s] = (acc[s] || 0) + 1; return acc;
    }, {});
    const statusPillColor = (s) => {
        const u = (s || "").toString().toUpperCase();
        if (u === "APPROVED") return "bg-emerald-50 text-emerald-700 border-emerald-200";
        if (u === "PENDING")  return "bg-amber-50 text-amber-700 border-amber-200";
        if (u === "REJECTED") return "bg-rose-50 text-rose-700 border-rose-200";
        if (u === "ALREADY_EXISTS") return "bg-slate-50 text-slate-700 border-slate-200";
        return "bg-slate-50 text-slate-600 border-slate-200";
    };
    const tplByName = tplStatus.reduce((acc, t) => { acc[t.name] = t; return acc; }, {});

    return (
        <div className="space-y-4" data-testid="wa-mapper">
            {!waConfigured && (
                <div className="ddi-card p-4 bg-amber-50 border-amber-200 flex items-start gap-2 text-[13px] text-[#B45309]">
                    <AlertTriangle size={16} className="mt-0.5" />
                    <div>Connect WhatsApp on the <b>Connections</b> tab first — template mappings need a live provider to list approved templates.</div>
                </div>
            )}

            {/* Templates catalog & live status header */}
            <Card>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                        <div className="text-[11px] uppercase tracking-widest text-[#0F766E] font-semibold">WABA template catalog</div>
                        <div className="text-sm font-bold text-[#123B5D] mt-0.5">
                            {tplStatus.length} templates · {Object.entries(statusCounts).map(([k, v]) => `${k}:${v}`).join(" · ") || "—"}
                        </div>
                        <p className="text-[12.5px] text-[#17212B]/70 mt-1 max-w-2xl">
                            Submit the shipped catalog to your WABA in one click. Rejected templates are dumped to <code className="text-[11px]">/app/memory/whatsapp_templates_for_panel.md</code> for manual paste.
                        </p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <button className="ddi-btn-outline text-sm" onClick={refreshStatuses} disabled={statusBusy}
                                data-testid="wa-tpl-refresh">
                            {statusBusy ? "Refreshing…" : "Refresh statuses"}
                        </button>
                        <button className="ddi-teal-btn text-sm" onClick={bulkSubmit} disabled={statusBusy}
                                data-testid="wa-tpl-bulk-submit">
                            Submit catalog
                        </button>
                        <button className="ddi-btn-outline text-sm" onClick={preWireMappings}
                                data-testid="wa-tpl-pre-wire">
                            Pre-wire mappings
                        </button>
                    </div>
                </div>
                {tplStatus.length > 0 && (
                    <div className="mt-4 border-t border-[#E5E9F2] pt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                        {tplStatus.map((t) => (
                            <div key={t.name} className="flex items-center justify-between gap-2 text-[12.5px] py-1"
                                 data-testid={`wa-tpl-status-row-${t.name}`}>
                                <div className="min-w-0 truncate">
                                    <span className="font-mono text-[#123B5D]">{t.name}</span>
                                    <span className="text-[#17212B]/50 ml-1">· {t.category || "—"}</span>
                                </div>
                                <span className={"text-[10.5px] uppercase font-semibold px-2 py-0.5 rounded-full border " + statusPillColor(t.live_status || t.submission_status)}>
                                    {(t.live_status || t.submission_status || "unknown").toString()}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
                {bulkResult?.panel_md_path && (
                    <div className="mt-3 text-[12px] text-[#B45309] bg-amber-50 border border-amber-200 rounded p-2">
                        Some templates need manual paste — see <code>{bulkResult.panel_md_path}</code>
                    </div>
                )}
            </Card>

            {/* One-click automation rule */}
            {(ruleState || []).map((r) => (
                <Card key={r.key}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                            <div className="text-[11px] uppercase tracking-widest text-[#0F766E] font-semibold">One-click automation</div>
                            <div className="text-sm font-bold text-[#123B5D] mt-0.5">{r.name}</div>
                            <p className="text-[12.5px] text-[#17212B]/70 mt-1 max-w-2xl">{r.description}</p>
                        </div>
                        <button
                            onClick={() => installRule(r.key)}
                            disabled={r.installed && r.enabled}
                            className={"text-sm rounded-full px-4 py-1.5 font-semibold " +
                                (r.installed && r.enabled
                                    ? "bg-[#F5F8FA] text-[#0F766E] border border-[#0F766E]/30"
                                    : "bg-[#0F766E] text-white hover:bg-[#0b615a]")}
                            data-testid={`automation-install-${r.key}`}
                        >
                            {r.installed && r.enabled ? "Installed · enabled" : "Install rule"}
                        </button>
                    </div>
                </Card>
            ))}

            {/* Mapping rows */}
            {rows.map((row) => {
                const draft = drafts[row.code] || { template_name: "", language: "en", variable_names: [] };
                const liveTpl = draft.template_name ? tplByName[draft.template_name] : null;
                const liveStatus = liveTpl?.live_status || liveTpl?.submission_status;
                return (
                    <Card key={row.code}>
                        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                            <div className="min-w-0">
                                <div className="text-[10px] uppercase tracking-widest text-[#17212B]/50">{row.stage}</div>
                                <div className="text-[15px] font-bold text-[#123B5D]">{row.label}</div>
                                <div className="text-[11px] text-[#17212B]/50 font-mono">{row.code}</div>
                            </div>
                            <div className="flex items-center gap-2">
                                {liveStatus && (
                                    <span className={"text-[10.5px] uppercase font-semibold px-2 py-0.5 rounded-full border " + statusPillColor(liveStatus)}
                                          data-testid={`wa-map-tpl-livestatus-${row.code}`}>
                                        Template: {liveStatus.toString()}
                                    </span>
                                )}
                                <StatusPill
                                    status={draft.template_name ? "connected" : "not_connected"}
                                    testId={`wa-map-status-${row.code}`}
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <Field label="WABA template">
                                <select className="ddi-input" value={draft.template_name || ""}
                                    onChange={(e) => updateDraft(row.code, { template_name: e.target.value })}
                                    data-testid={`wa-map-template-${row.code}`}>
                                    <option value="">— none (skip WhatsApp) —</option>
                                    {templates.map((t) => (
                                        <option key={t.name + t.language} value={t.name}>
                                            {t.name} · {t.language} · {t.status}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                            <Field label="Language">
                                <input className="ddi-input" value={draft.language || "en"}
                                    onChange={(e) => updateDraft(row.code, { language: e.target.value })}
                                    placeholder="en" data-testid={`wa-map-lang-${row.code}`} />
                            </Field>
                            <Field label="Available variables" hint="Reference — pick these when mapping placeholders below.">
                                <div className="text-[11.5px] text-[#17212B]/70 leading-relaxed pt-2">
                                    {(row.variables || []).map((v) => (
                                        <span key={v} className="inline-block bg-[#F5F8FA] rounded px-1.5 py-0.5 mr-1 mb-1 font-mono">{v}</span>
                                    ))}
                                </div>
                            </Field>
                        </div>
                        {draft.template_name ? (
                            <div className="mt-3">
                                <div className="text-[11px] uppercase tracking-widest text-[#17212B]/60 font-semibold mb-2">
                                    Map template variables — positional slot → touchpoint variable
                                </div>
                                <div className="space-y-1.5">
                                    {[0, 1, 2, 3, 4, 5].map((i) => {
                                        const current = draft.variable_names?.[i] || "";
                                        const shown = current || i < (draft.variable_names?.length || 0);
                                        if (!shown && i > (draft.variable_names?.length || 0)) return null;
                                        return (
                                            <div key={i} className="flex items-center gap-2 text-sm">
                                                <span className="font-mono text-[11px] text-[#17212B]/50 w-14 shrink-0">
                                                    {`{{${i + 1}}}`}
                                                </span>
                                                <select className="ddi-input flex-1" value={current}
                                                    onChange={(e) => updateVarAt(row.code, i, e.target.value)}
                                                    data-testid={`wa-map-var-${row.code}-${i}`}>
                                                    <option value="">— skip —</option>
                                                    {(row.variables || []).map((v) => (
                                                        <option key={v} value={v}>{v}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        );
                                    })}
                                    <button className="text-[11px] text-[#2563EB] font-semibold"
                                        onClick={() => updateDraft(row.code, { variable_names: [...(draft.variable_names || []), ""] })}
                                        data-testid={`wa-map-add-var-${row.code}`}>
                                        + Add another positional variable
                                    </button>
                                </div>
                            </div>
                        ) : null}
                        <div className="mt-4 flex flex-wrap items-center gap-2">
                            <button className="ddi-teal-btn text-sm" onClick={() => save(row.code)}
                                disabled={!!busy[`save-${row.code}`]}
                                data-testid={`wa-map-save-${row.code}`}>
                                <Save size={13} className="mr-1.5" /> {busy[`save-${row.code}`] ? "Saving…" : "Save"}
                            </button>
                            {row.template_name ? (
                                <button className="ddi-btn-outline text-sm" onClick={() => clear(row.code)}
                                    data-testid={`wa-map-clear-${row.code}`}>Clear mapping</button>
                            ) : null}
                            {draft.template_name ? (
                                <div className="flex items-center gap-2 ml-auto">
                                    <input className="ddi-input !w-40" placeholder="+91 test number"
                                        value={testTo[row.code] || ""}
                                        onChange={(e) => setTestTo((t) => ({ ...t, [row.code]: e.target.value }))}
                                        data-testid={`wa-map-test-to-${row.code}`} />
                                    <button className="ddi-btn-outline text-sm"
                                        onClick={() => testSend(row)}
                                        disabled={!!busy[`test-${row.code}`]}
                                        data-testid={`wa-map-test-${row.code}`}>
                                        {busy[`test-${row.code}`] ? "Sending…" : "Send test"}
                                    </button>
                                </div>
                            ) : null}
                        </div>
                        {row.updated_at ? (
                            <div className="text-[11px] text-[#17212B]/50 mt-2">
                                Last saved {new Date(row.updated_at).toLocaleString()} · {row.updated_by}
                            </div>
                        ) : null}
                    </Card>
                );
            })}
        </div>
    );
}

export default function SuperAdminCommunications() {
    const [tab, setTab] = useState("connections");
    return (
        <AdminShell>
            <div className="animate-fade-up" data-testid="communications-page">
                <div className="ddi-eyebrow text-[#0F766E]">Super Admin · Communications</div>
                <h1 className="mt-1.5 text-3xl md:text-[34px] font-bold text-[#123B5D] tracking-tight leading-[1.1]">
                    Communications Center
                </h1>
                <p className="mt-2 text-sm text-[#17212B]/70 leading-relaxed max-w-2xl">
                    Wire up outbound WhatsApp, email &amp; SMS providers, then decide exactly which touchpoints reach doctors on which channel. Failures never break the underlying flow — they land in the delivery log with a clear reason.
                </p>

                <div className="mt-6 border-b border-[#E5E9F2] flex gap-1 flex-wrap">
                    {TABS.map((t) => {
                        const I = t.icon;
                        const active = tab === t.id;
                        return (
                            <button key={t.id}
                                    className={"px-4 py-2.5 text-sm font-semibold flex items-center gap-2 border-b-2 transition-colors " +
                                        (active ? "border-[#0F766E] text-[#123B5D]" : "border-transparent text-[#64748B] hover:text-[#123B5D]")}
                                    onClick={() => setTab(t.id)}
                                    data-testid={`comms-tab-${t.id}`}>
                                <I size={15} /> {t.label}
                            </button>
                        );
                    })}
                </div>

                <div className="mt-6">
                    {tab === "connections" && <ConnectionsTab />}
                    {tab === "touchpoints" && <TouchpointsTab />}
                    {tab === "wa_templates" && <WaTemplateMapperTab />}
                    {tab === "delivery" && <DeliveryLogTab />}
                </div>
            </div>
        </AdminShell>
    );
}
